// @ts-nocheck
/*
 * Port of views/dashboard/voice-credits-logs/list.js (origin/main-dev, 363 lines).
 * BYTE-IDENTICAL LOGIC — state hooks, useEffects, tab-scoping (all/normal/
 * demo), summary rollup (additions/deductions/net/count), date filter,
 * client-side text search, and export payload all preserved.
 *
 * Chrome swaps: PrimeReact DataTable + Button â†’ obd3-gui DataTable +
 * shadcn Button; v1 <Card> â†’ shadcn <Card>.
 */
import React, { useState, useEffect } from 'react';
const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
import type { ColumnDef } from '@tanstack/react-table';
import { API_VOICE_CREDITS_LOGS } from '@/service/url';
import { getApiCall } from '@/service/httpCall';
import Loader from '@/components/partials/components/Loader';
import { getUser } from '@/utilities/setting';
import { hasPermission, formatRupeeAmount, exportToCSV } from '@/utilities/helpers';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import {
  Download,
  ArrowUp,
  ArrowDown,
  Wallet,
  ListChecks,
} from 'lucide-react';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const dateToInputStr = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const inputStrToLocalDate = (str) => {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!m) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString();
};

const VoiceCreditsLogs = ({
  logsApi = API_VOICE_CREDITS_LOGS,
  pageTitle = 'Voice Credits Logs',
  pageDescription = 'Additions and deductions of voice credits across companies.',
  exportFileName = 'voice-credits-logs',
  tableId = 'voice-credits-logs.page',
} = {}) => {
  const [logsList, setLogsList] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLogs, setFilteredLogs] = useState([]);

  const scopeByTab = (list) => {
    if (!Array.isArray(list)) return [];
    if (activeTab === 'demo') return list.filter((i) => i.is_demo_credits === true);
    if (activeTab === 'normal') return list.filter((i) => !i.is_demo_credits);
    return list;
  };
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState({});

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchLogs = async (start, end) => {
    try {
      setLoading(true);
      const url = `${logsApi}?startDate=${formatDate(start)}&endDate=${formatDate(end)}`;
      const resp = await getApiCall(url, {});
      const data = Array.isArray(resp) ? resp : [];
      setLogsList(data);
      setFilteredLogs(data);
    } catch (err) {
      console.error('Failed to fetch credits logs', err);
      setLogsList([]);
      setFilteredLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings?.module_operations) {
      try {
        setModuleOperationsForReadPermissionCheck(
          typeof user.acl_settings.module_operations === 'string'
            ? parseModuleOperations(user.acl_settings.module_operations)
            : user.acl_settings.module_operations,
        );
      } catch (e) {
        console.error(e);
      }
    }

    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setStartDate(firstDay);
    setEndDate(today);
    fetchLogs(firstDay, today);
  }, []);

  useEffect(() => {
    const scoped = scopeByTab(logsList);
    if (!searchTerm.trim()) {
      setFilteredLogs(scoped);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredLogs(
      scoped.filter((row) => {
        const performedBy = row.performed_by_username || row.performedByUser?.username || '';
        const fromName = row.from_company_name || row.fromCompany?.name || '';
        const toName = row.to_company_name || row.toCompany?.name || '';
        const action = row.action || '';
        return (
          performedBy.toLowerCase().includes(term) ||
          fromName.toLowerCase().includes(term) ||
          toName.toLowerCase().includes(term) ||
          action.toLowerCase().includes(term)
        );
      }),
    );
  }, [searchTerm, logsList, activeTab]);

  const cardSummary = (() => {
    const scoped = scopeByTab(logsList);
    let additions = 0;
    let deductions = 0;
    scoped.forEach((r) => {
      const amt = Number(r.effective_amount || 0);
      if (r.action === 'addition') additions += amt;
      else if (r.action === 'deduction') deductions += amt;
    });
    return { additions, deductions, net: additions - deductions, count: scoped.length };
  })();

  const handleFilter = () => {
    if (startDate && endDate) fetchLogs(startDate, endDate);
  };

  const handleReset = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setStartDate(firstDay);
    setEndDate(today);
    setSearchTerm('');
    fetchLogs(firstDay, today);
  };

  const handleExport = () => {
    if (!filteredLogs.length) return;
    exportToCSV({
      fileName: exportFileName,
      data: filteredLogs,
      columns: [
        { header: 'Date', accessor: (row) => formatDateTime(row.created_at) },
        {
          header: 'Performed By',
          accessor: (row) =>
            row.performed_by_username || row.performedByUser?.username || '-',
        },
        {
          header: 'From Company',
          accessor: (row) =>
            row.from_company_name || row.fromCompany?.name || '-',
        },
        {
          header: 'To Company',
          accessor: (row) =>
            row.to_company_name || row.toCompany?.name || '-',
        },
        { header: 'Base Amount', accessor: (row) => row.base_amount ?? '' },
        {
          header: 'Multiplier',
          accessor: (row) => `${row.multiplier ?? ''}x`,
        },
        {
          header: 'Effective Amount',
          accessor: (row) => row.effective_amount ?? '',
        },
        { header: 'Action', accessor: (row) => row.action || '-' },
        {
          header: 'From Balance After',
          accessor: (row) => row.from_balance_after ?? '-',
        },
        {
          header: 'To Balance After',
          accessor: (row) => row.to_balance_after ?? '',
        },
      ],
    });
  };

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    'Credit History',
    'read',
  );

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'created_at',
      header: 'Date & Time',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {formatDateTime(row.original.created_at)}
        </span>
      ),
    },
    {
      id: 'performed_by',
      header: 'Performed By',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">
          {row.original.performed_by_username ||
            row.original.performedByUser?.username ||
            '—'}
        </span>
      ),
    },
    {
      id: 'from_company',
      header: 'From Company',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">
          {row.original.from_company_name || row.original.fromCompany?.name || '—'}
        </span>
      ),
    },
    {
      id: 'to_company',
      header: 'To Company',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">
          {row.original.to_company_name || row.original.toCompany?.name || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'base_amount',
      header: 'Base Amount',
      cell: ({ row }) => (
        <span className="font-mono text-xs tabular-nums text-foreground">
          {formatRupeeAmount(row.original.base_amount)}
        </span>
      ),
    },
    {
      accessorKey: 'multiplier',
      header: 'Multiplier',
      cell: ({ row }) => (
        <span className="inline-flex items-center rounded-md bg-muted/60 px-2 py-0.5 font-mono text-xs font-semibold tabular-nums text-foreground ring-1 ring-inset ring-border/60">
          {row.original.multiplier}x
        </span>
      ),
    },
    {
      accessorKey: 'effective_amount',
      header: 'Effective Amount',
      cell: ({ row }) => (
        <span className="font-mono text-xs font-semibold tabular-nums text-foreground">
          {formatRupeeAmount(row.original.effective_amount)}
        </span>
      ),
    },
    {
      id: 'demo',
      header: 'Demo',
      size: 90,
      cell: ({ row }) =>
        row.original.is_demo_credits ? (
          <span className="inline-flex items-center whitespace-nowrap rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300">
            Demo
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'action',
      header: 'Action',
      size: 130,
      cell: ({ row }) => {
        const isAdd = row.original.action === 'addition';
        return (
          <span
            className={
              'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
              (isAdd
                ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
            }
          >
            {isAdd ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
            <span className="capitalize">{row.original.action}</span>
          </span>
        );
      },
    },
    {
      accessorKey: 'to_balance_after',
      header: 'Target Balance After',
      cell: ({ row }) => (
        <span className="font-mono text-xs tabular-nums text-foreground">
          {formatRupeeAmount(row.original.to_balance_after)}
        </span>
      ),
    },
  ];

  if (getUser()?.assignedCompany?.is_own_company === true) {
    return (
      <div className="text-center p-4" style={{ marginTop: '50px', fontSize: '16px' }}>
        Voice credits logs are not available for your account.
      </div>
    );
  }

  if (Object.keys(moduleOperationsForReadPermissionCheck).length > 0 && !canRead) {
    return (
      <div className="text-center p-4" style={{ marginTop: '50px', fontSize: '16px' }}>
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading && logsList.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title={pageTitle}
        description={pageDescription}
        actions={
          filteredLogs.length > 0 ? (
            <Button size="sm" onClick={handleExport} className="gap-1">
              <Download className="h-4 w-4" />
              Export Data
            </Button>
          ) : null
        }
      />
      {/* Compact stat strip — Additions / Deductions / Net / Entries. */}
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 rounded-md border border-border/50 bg-card px-3 py-2 text-xs shadow-neu-raised">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
            <ArrowUp className="h-4 w-4" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] text-muted-foreground">Total additions</span>
            <span className="text-sm font-bold text-foreground tabular-nums">
              {formatRupeeAmount(cardSummary.additions)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-md border border-border/50 bg-card px-3 py-2 text-xs shadow-neu-raised">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
            <ArrowDown className="h-4 w-4" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] text-muted-foreground">Total deductions</span>
            <span className="text-sm font-bold text-foreground tabular-nums">
              {formatRupeeAmount(cardSummary.deductions)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-md border border-border/50 bg-card px-3 py-2 text-xs shadow-neu-raised">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
            <Wallet className="h-4 w-4" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] text-muted-foreground">Net</span>
            <span
              className={
                'text-sm font-bold tabular-nums ' +
                (cardSummary.net >= 0
                  ? 'text-emerald-700 dark:text-emerald-400'
                  : 'text-rose-700 dark:text-rose-400')
              }
            >
              {formatRupeeAmount(cardSummary.net)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-md border border-border/50 bg-card px-3 py-2 text-xs shadow-neu-raised">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
            <ListChecks className="h-4 w-4" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] text-muted-foreground">Entries</span>
            <span className="text-sm font-bold text-foreground tabular-nums">
              {cardSummary.count}
            </span>
          </div>
        </div>
      </div>

      {/* No outer Card wrapper — DataTable already ships a rounded
          border container, so nesting was creating a second edge. */}
      <div className="mb-3 flex flex-wrap items-end gap-3">
        {/* Segmented type filter */}
          <div>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Type
            </label>
            <div
              className="inline-flex h-10 items-center gap-1 rounded-full border border-border/60 bg-card p-1"
              style={{
                boxShadow:
                  'inset 2px 2px 5px rgba(163,177,198,0.35), inset -2px -2px 5px rgba(255,255,255,0.9)',
              }}
            >
              {[
                { key: 'all', label: 'All' },
                { key: 'normal', label: 'Standard' },
                { key: 'demo', label: 'Demo' },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  className={
                    'rounded-full px-4 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 ' +
                    (activeTab === t.key
                      ? 'bg-primary text-primary-foreground shadow-[0_2px_5px_rgba(0,0,0,0.15)]'
                      : 'text-muted-foreground hover:bg-primary/10 hover:text-primary')
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              From
            </label>
            <CalendarDateInput
              value={dateToInputStr(startDate)}
              onChange={(e) => setStartDate(inputStrToLocalDate(e))}
              style={{ height: '40px', width: '170px' }}
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              To
            </label>
            <CalendarDateInput
              value={dateToInputStr(endDate)}
              onChange={(e) => setEndDate(inputStrToLocalDate(e))}
              style={{ height: '40px', width: '170px' }}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleFilter}>Filter</Button>
            <Button onClick={handleReset} variant="outline">
              Reset
            </Button>
          </div>
        </div>

      <DataTable
        tableId={tableId}
        columns={columns}
        data={filteredLogs}
        searchPlaceholder="Search by user, company, or action…"
        emptyTitle="No logs"
        emptyDescription="No logs found for the current filters."
        pageSize={25}
        disableExport
      />
    </FillPage>
  );
};

export default VoiceCreditsLogs;
